// neo/helpers — formatting, status vocab (EXACT German labels → DS tones), and small shared
// UI atoms. Ported from the design prototype (neo/helpers.jsx) to a typed ES module.
import React from "react";
import { StatusPill, Tag, type StatusPillVariant, type StatusTone } from "@/ds";
import { Icon } from "./icons";
import { cx } from "./cx";
import { useT } from "../i18n";

export { cx };

/** The status vocabulary tone. One definition, in the design system — two copies drift. */
export type Tone = StatusTone;

// ---- Number / date formatting (tabular, German) ----
const _eur = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const _num = new Intl.NumberFormat("de-DE");
export const eur = (n: number | null | undefined): string => (n == null ? "—" : _eur.format(n));
export const num = (n: number | null | undefined): string => (n == null ? "—" : _num.format(n));

export function fmtDate(d: Date | string | number | null | undefined): string {
  if (!d) return "—";
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt as unknown as number)) return String(d);
  const p = (x: number) => String(x).padStart(2, "0");
  return `${p(dt.getDate())}.${p(dt.getMonth() + 1)}.${dt.getFullYear()}`;
}
export function fmtDateShort(d: Date | string | number): string {
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt as unknown as number)) return String(d);
  const p = (x: number) => String(x).padStart(2, "0");
  return `${p(dt.getDate())}.${p(dt.getMonth() + 1)}.`;
}
export function fmtTime(d: Date | string | number): string {
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt as unknown as number)) return String(d);
  const p = (x: number) => String(x).padStart(2, "0");
  return `${p(dt.getHours())}:${p(dt.getMinutes())}`;
}
export const fmtStamp = (d: Date | string | number): string => `${fmtDateShort(d)} ${fmtTime(d)}`;

// ---- Status vocabularies ----
// Tones/icons are language-neutral and live here; the human labels live in the i18n dictionary
// under `status.<kind>.<code>` and are resolved with the current language via useT().
export const LIFECYCLE_TONE: Record<string, Tone> = {
  new: "info", extracting: "progress", needs_information: "warning", ready_for_draft: "info",
  draft_generating: "progress", draft_pending_review: "warning", sending: "progress", sent: "success",
  needs_human_review: "warning", closed: "neutral", failed: "danger",
};
export const LIFECYCLE_CODES = Object.keys(LIFECYCLE_TONE);
export const INBOX_STATUS_TONE: Record<string, Tone> = {
  new: "info",
  in_progress: "progress",
  answered: "success",
  closed: "neutral",
};
export const RESPONSE_TONE: Record<string, Tone> = {
  minimum_order_online_shop: "neutral", follow_up: "info", information_request: "info",
  handover_human: "warning", acknowledgement: "success",
};
export const FACT_TONE: Record<string, Tone> = {
  unknown: "neutral", extracted: "info", confirmed: "success", ambiguous: "warning",
  conflicting: "danger", externally_resolved: "info", curated_venue_resolved: "success",
  manually_overridden: "neutral",
};
export const FACT_SOURCE_ICON: Record<string, string> = {
  customer_email: "mail", external_lookup: "external", manual: "pencil", rule_inference: "gauge",
};
export const REVIEW_TONE: Record<string, Tone> = {
  pending: "warning", edited: "info", approved: "success", rejected: "danger",
};
export const SEND_TONE: Record<string, Tone> = {
  IDLE: "neutral", SENDING: "progress", SENT: "success", UNKNOWN: "warning",
  FAILED_BEFORE_SUBMISSION: "danger",
};
export const CATERING_ICON: Record<string, string> = {
  fingerfood: "utensils", menu: "utensils", buffet: "utensils", bbq: "flame",
  trade_fair: "building", live_cooking: "flame",
};
const TONE_REG: Record<string, Record<string, Tone>> = {
  lifecycle: LIFECYCLE_TONE, inbox: INBOX_STATUS_TONE, response: RESPONSE_TONE, fact: FACT_TONE,
  review: REVIEW_TONE, send: SEND_TONE,
};

// <StatusOf kind="lifecycle" value="sent" /> — DS StatusPill with a translated label + tone
export function StatusOf({ kind, value, size, variant, dot, icon }: {
  kind: keyof typeof TONE_REG | string;
  value: string;
  size?: "sm" | "md";
  variant?: StatusPillVariant;
  dot?: boolean;
  icon?: React.ReactNode;
}) {
  const t = useT();
  const tone = (TONE_REG[kind] || {})[value];
  if (!tone) return null;
  return <StatusPill tone={tone} label={t(`status.${kind}.${value}`, undefined, value)} size={size} variant={variant} dot={dot} icon={icon} />;
}

// ---- Small UI atoms ----
export function Eyebrow({ children, style, className }: { children?: React.ReactNode; style?: React.CSSProperties; className?: string }) {
  return <div className={cx("neo-eyebrow", className)} style={style}>{children}</div>;
}
export function Mono({ children, className, style }: { children?: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  return <span className={cx("neo-mono", className)} style={style}>{children}</span>;
}
// labelled key/value — label left (muted), value right (tabular-friendly)
export function KeyVal({ label, children, icon, mono, align = "right" }: {
  label?: React.ReactNode; children?: React.ReactNode; icon?: string; mono?: boolean; align?: React.CSSProperties["textAlign"];
}) {
  return (
    <div className="neo-kv">
      <span className="neo-kv__k">{icon && <Icon name={icon} size={14} />}{label}</span>
      <span className={cx("neo-kv__v", mono && "neo-mono")} style={{ textAlign: align }}>{children}</span>
    </div>
  );
}
export function Eyerow({ icon, children }: { icon?: string; children?: React.ReactNode }) {
  return <span className="neo-eyerow">{icon && <Icon name={icon} size={13} />}{children}</span>;
}
export function CateringChip({ type }: { type: string }) {
  const t = useT();
  const icon = CATERING_ICON[type];
  if (!icon) return null;
  return <Tag icon={<Icon name={icon} size={13} />}>{t(`status.catering.${type}`, undefined, type)}</Tag>;
}
// section header used inside cards/panels
export function PanelLabel({ icon, children, hint, action }: {
  icon?: string; children?: React.ReactNode; hint?: React.ReactNode; action?: React.ReactNode;
}) {
  return (
    <div className="neo-panellabel">
      <span className="neo-panellabel__t">{icon && <Icon name={icon} size={15} />}{children}</span>
      {hint && <span className="neo-panellabel__hint">{hint}</span>}
      {action && <span className="neo-panellabel__action">{action}</span>}
    </div>
  );
}
